import { CloudOff } from "@mui/icons-material";
import { QueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { AppStorage } from "~/src/contexts/StorageContext/StorageProvider";
import { ArtType } from "~/src/helpers/types";
import { ClientState, Individ, IndividResponse } from "../../api/types";
import { getKjonnOgAlderByArt } from "../../helpers/artHelper";
import { getFirstAndLastName } from "../../helpers/stringHelper";
import { getReadableEnumLowercase, isMissingOvervakData } from "../../pages/Oversikt/helpers";
import { MalemetodeSlaktevekt } from "../../pages/Registrering/types";
import BottomLine from "../BottomLine";
import {
  AddButtonIcon,
  ArrowRightIcon,
  CompleteIcon,
  SyncErrorIcon,
  SyncIcon,
  WarningIcon,
} from "../Icons/Icons";
import Text, { Header } from "../Text/Text";

type SkuttDyrCardProps = {
  skuttDyr: IndividResponse;
  syncStatus?: "pending" | "error" | "unconfirmed";
  isLoading?: boolean;
  storage: AppStorage;
  queryClient: QueryClient;
  clientState: ClientState;
};

export const detailsItem = (label: string, value?: string | null) => {
  return (
    <li
      key={label}
      className={`text-lg py-2 px-2 ${
        value ? "bg-[#CFDFE4]" : "bg-ldir-orange-700"
      } border-t border-ldir-blue-pale`}
    >
      <dl className="flex flex-wrap">
        <dt>{label}:</dt>
        <dd className=" ml-2">{value || "?"}</dd>
      </dl>
    </li>
  );
};
export default function SkuttDyrCard({
  skuttDyr,
  syncStatus,
  isLoading,
  queryClient,
  clientState,
  storage,
}: SkuttDyrCardProps): JSX.Element {
  const { t } = useTranslation();
  const ikkeVeid = `${t("pages.registrerArt.omSkuttDyr.ikkeVeid")}`;
  const [firstName, lastName] = getFirstAndLastName(skuttDyr.RegistrertAv);

  const showOfflineNoData =
    queryClient?.getQueryData(["individId-" + skuttDyr.Id, { individId: skuttDyr.Id }]) ===
      undefined &&
    storage.get<Individ>({ id: skuttDyr.Id }, "Individ") === undefined &&
    clientState != "Online" &&
    import.meta.env.MODE !== "prod";

  return (
    <div
      className={`relative  text-ldir-brown-900 font-normal text-base bg-ldir-blue-100 ${
        isLoading ? "animate-pulse pointer-events-none" : ""
      }`}
    >
      <NavLink to={`/oversikt/skuttdyrdetaljer/${skuttDyr.Art}/${skuttDyr.Id}`}>
        {(syncStatus === "pending" || syncStatus === "error") && (
          <>
            <BottomLine />
            <div
              className={`py-2 px-4 ${
                syncStatus === "pending" ? "bg-ldir-blue-500" : "bg-ldir-red-700"
              } flex gap-2`}
            >
              {syncStatus === "pending" && (
                <>
                  <SyncIcon
                    className="text-ldir-blue-900"
                    title="Individ har enda ikke blitt overført til Hjorteviltregisteret"
                  />
                  <Text>Venter på overføring</Text>
                </>
              )}
              {syncStatus === "error" && (
                <>
                  <SyncErrorIcon className="text-ldir-red-900" title="Feil ved overføring" />
                  <Text>Feil ved overføring</Text>
                </>
              )}
            </div>
          </>
        )}
        <div className="flex border-t border-ldir-blue-pale font-bold text-xl mb-4 active:bg-ldir-blue-100 pt-4 px-4 items-center">
          {skuttDyr.Godkjent && (
            <CompleteIcon title={t("pages.oversikt.dyretErGodkjent")} className="mr-2" />
          )}
          <Header headerLvl={3}>
            {skuttDyr.Dato && format(skuttDyr.Dato || "", "dd.MM.", { locale: nb })} {skuttDyr.Art}{" "}
            {getKjonnOgAlderByArt(skuttDyr)}
          </Header>
          <div className="ml-auto flex">
            {showOfflineNoData && <CloudOff className="text-ldir-blue-900 mr-2" />}
            <ArrowRightIcon />
          </div>
        </div>
        <div className="mx-4 flex">
          {(skuttDyr.UtenforJakttid || isMissingOvervakData(skuttDyr)) && (
            <div className="relative">
              {skuttDyr.UtenforJakttid && isMissingOvervakData(skuttDyr) && (
                <div className=" bg-ldir-orange-900 rounded-full m-0 px-1 border-ldir-blue-100 border right-0 -top-0.5 text-ldir-blue-100 text-xs absolute">
                  2
                </div>
              )}
              <WarningIcon
                className="mr-2 mb-2"
                title={`${
                  skuttDyr.UtenforJakttid ? t("pages.oversikt.skuttDyrUtenforJakttid") : ""
                } ${isMissingOvervakData(skuttDyr) ? t("pages.oversikt.skuttIOvervakomrade") : ""}`}
              />
            </div>
          )}
        </div>
        <ul className={`mx-4`}>
          {detailsItem(
            t("components.skuttDyrCard.registrertAv"),
            firstName && `${firstName.substring(0, 1)}. ${lastName}.`,
          )}
          {skuttDyr.Art === ArtType.Hjort &&
            detailsItem(t("components.skuttDyrCard.terreng"), skuttDyr.Terreng)}
          {detailsItem(
            t("components.skuttDyrCard.weight"),
            skuttDyr.SlaktevektKg
              ? `${skuttDyr.SlaktevektKg} kg, ${getReadableEnumLowercase(
                  skuttDyr.MålemetodeSlaktevekt,
                )}`
              : skuttDyr.MålemetodeSlaktevekt === MalemetodeSlaktevekt.IkkeVeid
                ? ikkeVeid
                : undefined,
          )}
          {detailsItem(t("components.skuttDyrCard.tagNo"), skuttDyr.Merkelapp)}
          {skuttDyr.Art === ArtType.Hjort &&
            (skuttDyr.Termisk === null || skuttDyr.Termisk === undefined) &&
            detailsItem(t("components.skuttDyrCard.termisk"), undefined)}
        </ul>
        {skuttDyr.Godkjent && (
          <div className="mx-4 mb-3">
            <BottomLine />
          </div>
        )}
      </NavLink>
      {!skuttDyr.Godkjent && (
        <div className="mx-4">
          <BottomLine />
          <NavLink to={`/registrering/fellingsinfo/${skuttDyr.Id}`}>
            <div className="bg-ldir-blue-100 w-full active:bg-ldir-blue-100 flex py-2 items-center mt-2">
              <AddButtonIcon />
              <span className="ml-2 text-xl text-ldir-blue-900">
                {t("components.skuttDyrCard.addInfo")}
              </span>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  );
}
